import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PUBLIC_NAV_ITEMS, isNavItemVisible } from '@/constants/user-menu'
import { useSiteBrand, useTenantFeatures, usePortalVisibility } from '@/hooks'

interface SiteFooterProps {
  /** 鉴权页:紧跟表单卡片,无分割线 */
  embedded?: boolean
  /** 全站页脚(带顶部分割线) */
  compact?: boolean
}

export function SiteFooter({ embedded, compact }: SiteFooterProps) {
  const { t } = useTranslation()
  const { isEnabled } = useTenantFeatures()
  const { isPortalEnabled } = usePortalVisibility()
  const { showFooterSiteName, siteName, tagline, copyright, icpLink, psbLink } = useSiteBrand()
  const footerLinks = PUBLIC_NAV_ITEMS.filter((item) =>
    isNavItemVisible(item, isEnabled, isPortalEnabled),
  )

  return (
    <footer
      className={
        embedded
          ? 'mt-8 w-full max-w-sm px-2 text-center'
          : compact
            ? 'shrink-0 border-t border-border bg-background px-4 py-5'
            : 'border-t border-border bg-card px-6 py-8'
      }
    >
      <div className={embedded ? undefined : 'mx-auto max-w-6xl text-center'}>
        {showFooterSiteName ? (
          <p className="text-sm font-medium text-zinc-300">{siteName}</p>
        ) : null}
        <p className={showFooterSiteName ? 'mt-1 text-xs text-zinc-500' : 'text-xs text-zinc-500'}>
          {tagline}
        </p>

        <p
          data-testid="site-footer-copyright"
          className="mt-3 text-[12px] leading-relaxed text-zinc-300"
        >
          {copyright}
        </p>

        {!embedded && footerLinks.length > 0 ? (
          <nav
            className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs"
            aria-label={t('site.footer.explore')}
          >
            {footerLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-zinc-400 transition hover:text-indigo-300"
              >
                {t(item.i18nKey)}
              </Link>
            ))}
          </nav>
        ) : null}

        {(icpLink || psbLink) && (
          <p className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
            {icpLink && (
              <a
                href={icpLink.url}
                target="_blank"
                rel="noopener noreferrer"
                className="transition hover:text-indigo-300"
              >
                {icpLink.number}
              </a>
            )}
            {icpLink && psbLink && <span aria-hidden>·</span>}
            {psbLink &&
              (psbLink.url ? (
                <a
                  href={psbLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 transition hover:text-indigo-300"
                >
                  {psbLink.number}
                </a>
              ) : (
                <span>{psbLink.number}</span>
              ))}
          </p>
        )}
      </div>
    </footer>
  )
}
